import {createReducer, on} from '@ngrx/store';
import {ProfileModel} from "../../../models/profile.model";
import {
  ClearAutoGeneratedUsername,
  ClearLoading,
  ClearSelectedUserData,
  ClearState,
  ClearWeekInfoForContract,
  EditProfileByUserIdFailure,
  EditProfileByUserIdRequest,
  EditProfileByUserIdSuccess,
  EditProfileFailure,
  EditProfileRequest,
  EditProfileSuccess,
  FetchAvatarFilterFailure,
  FetchAvatarFilterRequest,
  FetchAvatarFilterSuccess,
  FetchGenderFilterFailure,
  FetchGenderFilterRequest,
  FetchGenderFilterSuccess,
  FetchLatestNotificationForPopupFailure,
  FetchLatestNotificationForPopupRequest,
  FetchLatestNotificationForPopupSuccess,
  FetchNotificationDetailsFailure,
  FetchNotificationDetailsRequest,
  FetchNotificationDetailsSuccess,
  FetchProfileByUserIdFailure,
  FetchProfileByUserIdRequest,
  FetchProfileByUserIdSuccess,
  FetchProfileFailure,
  FetchProfileRequest,
  FetchProfileSuccess,
  FetchUserStatusFilterFailure,
  FetchUserStatusFilterRequest,
  FetchUserStatusFilterSuccess,
  GenerateOtpFailure,
  GenerateOtpRequest,
  GenerateOtpSuccess,
  GenerateUsernameFailure,
  GenerateUsernameRequest,
  GenerateUsernameSuccess,
  OtpSentStatus,
  SaveAnswersFailure,
  SaveAnswersRequest,
  SaveAnswersSuccess,
  SetDrillDownUrls,
  SetProfile,
  SetScreenSize,
  SetScreenType,
  SetWeekInfoForContract,
  StartLoading,
  StopLoading,
  UpdateActiveGroupDetailsOnProfile,
  VerifyOtpFailure,
  VerifyOtpRequest,
  VerifyOtpSuccess,
  VerifyUsernameFailure,
  VerifyUsernameRequest,
  VerifyUsernameSuccess
} from "./shared.actions";
import {Filter} from "../../../models/filter";
import {
  NotificationItemModel
} from "../../page-modules/notifications/model/notification-model";

// State for this feature (User)
export interface SharedState {
  loading: number[];
  profileData: {
    profile: ProfileModel;
    loading: boolean
  };
  selectedUserProfileData: {
    profile: ProfileModel;
    loading: boolean
  };
  viewType: 'mobile' | 'tablet' | 'desktop' | '',
  screenSize: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '',
  drillDownUrls: string[],
  autoGenerateUsername: {
    loading: boolean,
    username: string,
    uuid: string
  },
  verifyUsername: {
    loading: boolean,
    isAvailable: boolean,
    uuid: string,
  },
  saveAnswerInfo: {
    loading: boolean
  },
  genderInfo: Filter,
  userStatusInfo: Filter,
  generateOtpInfo: {
    loading: boolean,
    otpSent: boolean,
  };
  verifyOtpInfo: {
    loading: boolean,
  },
  notificationDetails: {
    details: NotificationItemModel,
    loading: boolean
  },
  notificationCurrentTimestamp: any,
  weekInfoForContract: {
    weekNumber: number,
    sessionNumber: number,
    weekEndDate: string,
    weekStartDate: string,
    previousUrl: string,
  },
  avatarFilter: {
    loading: boolean,
    avatarOptions: any
  },
}

const initialState: SharedState = {
  loading: [],
  profileData: {
    profile: null,
    loading: false
  },
  selectedUserProfileData: {
    profile: null,
    loading: false
  },
  viewType: '',
  screenSize: '',
  drillDownUrls: [],
  autoGenerateUsername: {
    loading: false,
    username: '',
    uuid: ''
  },
  verifyUsername: {
    loading: false,
    isAvailable: false,
    uuid: ''
  },
  saveAnswerInfo: {
    loading: false
  },
  genderInfo: {
    list: [],
    total: null,
    pageNumber: 1,
    limit: 10,
    hasNext: false,
    hasPrev: false,
    loading: false
  },
  userStatusInfo: {
    list: [],
    total: null,
    pageNumber: 1,
    limit: 10,
    hasNext: false,
    hasPrev: false,
    loading: false
  },
  generateOtpInfo: {
    loading: false,
    otpSent: false
  },
  verifyOtpInfo: {
    loading: false,
  },
  notificationDetails: {
    details: {},
    loading: false
  },
  notificationCurrentTimestamp: null,
  weekInfoForContract: {
    weekNumber: 0,
    sessionNumber: 0,
    weekEndDate: '',
    weekStartDate: '',
    previousUrl: '',
  },
  avatarFilter: {
    loading: false,
    avatarOptions: []
  },
};

export const reducer = createReducer(
  initialState,


  // START LOADING
  on(StartLoading, (state) => ({
    ...state,
    loading: [...state.loading, 1]
  })),

  // CLEAR STATE
  on(ClearState, (state) => ({
    ...initialState,
    screenSize: state.screenSize,
    viewType: state.viewType
  })),

  // STOP LOADING
  on(StopLoading, (state) => ({
    ...state,
    loading: [...state.loading].slice(1),
  })),

  // CLEAR LOADING
  on(ClearLoading, (state) => ({
    ...state,
    loading: [],
  })),

  // CLEAR SELECTED USER DATA
  on(ClearSelectedUserData, (state) => ({
    ...state,
    selectedUserProfileData: initialState.selectedUserProfileData,
  })),


  // SET PROFILE
  on(SetProfile, (state, props) => {
    return ({
      ...state,
      profileData: {
        ...state.profileData,
        profile: props.profile,
      }
    });
  }),

  // SET SCREEN TYPE
  on(SetScreenType, (state, props) => ({
    ...state,
    viewType: props.screenType
  })),

  //  SET SCREEN SIZE
  on(SetScreenSize, (state, props) => ({
    ...state,
    screenSize: props.screenSize
  })),

  // SET DRILL DOWN URLs
  on(SetDrillDownUrls, (state, props) => ({
    ...state,
    drillDownUrls: props.payload
  })),

  // FETCH PROFILE
  on(FetchProfileRequest, (state) => ({
    ...state,
    profileData: {
      ...state.profileData,
      loading: true,
    }
  })),
  on(FetchProfileSuccess, (state, props) => ({
    ...state,
    profileData: {
      loading: false,
      profile: props.profile
    }
  })),
  on(FetchProfileFailure, (state) => ({
    ...state,
    profileData: {
      ...state.profileData,
      loading: false
    }
  })),

  // FETCH PROFILE BY USER ID
  on(FetchProfileByUserIdRequest, (state) => ({
    ...state,
    selectedUserProfileData: {
      ...state.selectedUserProfileData,
      loading: true,
    }
  })),
  on(FetchProfileByUserIdSuccess, (state, props) => ({
    ...state,
    selectedUserProfileData: {
      loading: false,
      profile: props.profile
    }
  })),
  on(FetchProfileByUserIdFailure, (state) => ({
    ...state,
    selectedUserProfileData: {
      ...state.selectedUserProfileData,
      loading: false
    }
  })),

  // EDIT PROFILE
  on(EditProfileRequest, (state) => ({
    ...state,
    profileData: {
      ...state.profileData,
      loading: true
    }
  })),
  on(EditProfileSuccess, (state, props) => ({
    ...state,
    profileData: {
      loading: false,
      profile: props.profile
    }
  })),
  on(EditProfileFailure, (state) => ({
    ...state,
    profileData: {
      ...state.profileData,
      loading: false
    }
  })),

  // EDIT PROFILE BY USER ID
  on(EditProfileByUserIdRequest, (state) => ({
    ...state,
    selectedUserProfileData: {
      ...state.selectedUserProfileData,
      loading: true,
    }
  })),
  on(EditProfileByUserIdSuccess, (state, props) => ({
    ...state,
    selectedUserProfileData: {
      loading: false,
      profile: props.profile
    }
  })),
  on(EditProfileByUserIdFailure, (state) => ({
    ...state,
    selectedUserProfileData: {
      ...state.selectedUserProfileData,
      loading: false
    }
  })),

  //GENDER
  on(FetchGenderFilterRequest, (state) => ({
    ...state,
    genderInfo: {
      ...initialState.genderInfo,
      loading: true,
    }

  })),
  on(FetchGenderFilterSuccess, (state, props) => (
    {
      ...state,
      genderInfo: {
        ...props.genderList,
        loading: false,
      },

    })),
  on(FetchGenderFilterFailure, (state) => ({
    ...state,
    genderInfo: {
      ...initialState.genderInfo,
      loading: false,
    }
  })),

  // AUTO GENERATE USERNAME
  on(GenerateUsernameRequest, (state, props) => ({
    ...state,
    autoGenerateUsername: {
      loading: true,
      username: '',
      uuid: props?.payload?.uuid
    }
  })),
  on(GenerateUsernameSuccess, (state, props) => ({
    ...state,
    autoGenerateUsername: {
      loading: false,
      username: props.username,
      uuid: props?.uuid
    }
  })),
  on(GenerateUsernameFailure, (state) => ({
    ...state,
    autoGenerateUsername: {
      ...state.autoGenerateUsername,
      loading: false,
      username: '',
    }
  })),

  // CLEAR AUTO GENERATED USERNAME
  on(ClearAutoGeneratedUsername, (state) => ({
    ...state,
    autoGenerateUsername: {
      ...initialState.autoGenerateUsername
    }
  })),

  // VERIFY USERNAME
  on(VerifyUsernameRequest, (state, props) => ({
    ...state,
    verifyUsername: {
      ...initialState.verifyUsername,
      loading: true,
      uuid: props.payload.uuid
    }
  })),
  on(VerifyUsernameSuccess, (state, props) => ({
    ...state,
    verifyUsername: {
      loading: false,
      isAvailable: props.isAvailable,
      uuid: props.uuid
    }
  })),
  on(VerifyUsernameFailure, (state) => ({
    ...state,
    verifyUsername: {
      ...state.verifyUsername,
      loading: false,
    }
  })),

  // SAVE ANSWERS
  on(SaveAnswersRequest, (state) => ({
    ...state,
    saveAnswerInfo: {
      loading: true,
    },
  })),
  on(SaveAnswersSuccess, (state, props) => ({
    ...state,
    ...props
  })),
  on(SaveAnswersFailure, (state) => ({
    ...state,
    saveAnswerInfo: {
      loading: false,
    }
  })),

  // GENERATE OTP
  on(GenerateOtpRequest, (state) => ({
    ...state,
    generateOtpInfo: {
      loading: true,
      otpSent: false
    }
  })),
  on(GenerateOtpSuccess, (state) => ({
    ...state,
    generateOtpInfo: {
      loading: false,
      otpSent: true
    }
  })),
  on(GenerateOtpFailure, (state) => ({
    ...state,
    generateOtpInfo: {
      loading: false,
      otpSent: false
    }
  })),

  // VERIFY OTP
  on(VerifyOtpRequest, (state) => ({
    ...state,
    verifyOtpInfo: {
      loading: true
    }
  })),
  on(VerifyOtpSuccess, (state) => ({
    ...state,
    verifyOtpInfo: {
      loading: false
    }
  })),
  on(VerifyOtpFailure, (state) => ({
    ...state,
    verifyOtpInfo: {
      loading: false
    }
  })),

  // OTP SENT STATUS
  on(OtpSentStatus, (state, props) => ({
    ...state,
    generateOtpInfo: {
      loading: false,
      otpSent: props.otpSent
    }
  })),

  //notifications
  on(FetchNotificationDetailsRequest, (state) => ({
    ...state, notificationDetails: {
      details: {}, loading: true
    }
  })),

  on(FetchNotificationDetailsSuccess, (state, props) => ({
    ...state, notificationDetails: {
      details: props.payload.notificationDetails, loading: false
    }
  })),

  on(FetchNotificationDetailsFailure, (state) => ({
    ...state, notificationDetails: {
      details: {}, loading: false
    }
  })),

  //fetch latest notifications
  on(FetchLatestNotificationForPopupRequest, (state) => ({
    ...state,
  })),

  on(FetchLatestNotificationForPopupSuccess, (state, props) => ({
    ...state,
    notificationCurrentTimestamp: props.payload.currentTimestamp
  })),

  on(FetchLatestNotificationForPopupFailure, (state) => ({
    ...state
  })),

  //FETCH USER FILTER
  on(FetchUserStatusFilterRequest, (state) => ({
    ...state,
    userStatusInfo: {
      ...initialState.genderInfo,
      loading: true,
    }

  })),
  on(FetchUserStatusFilterSuccess, (state, props) => (
    {
      ...state,
      userStatusInfo: {
        ...props.userStatusList,
        loading: false,
      },

    })),
  on(FetchUserStatusFilterFailure, (state) => ({
    ...state,
    userStatusInfo: {
      ...initialState.genderInfo,
      loading: false,
    }
  })),

  // SET WEEK INFO FOR CONTRACT
  on(SetWeekInfoForContract, (state, props) => ({
    ...state,
    weekInfoForContract: {
      weekNumber: props.weekNumber,
      sessionNumber: props.sessionNumber,
      weekStartDate: props.weekStartDate,
      weekEndDate: props.weekEndDate,
      previousUrl: props.previousUrl,
    }
  })),

  // CLEAR WEEK INFO FOR CONTRACT
  on(ClearWeekInfoForContract, (state) => ({
    ...state,
    weekInfoForContract: initialState.weekInfoForContract
  })),

  // UPDATE ACTIVE GROUP DETAILS ON PROFILE
  on(UpdateActiveGroupDetailsOnProfile, (state, props) => (
    {
      ...state,
      profileData: {
        ...state.profileData,
        profile: {
          ...state.profileData.profile,
          ...props
        },
      }
    })),


  //fetch avatar options
  on(FetchAvatarFilterRequest, (state) => ({
    ...state,
    avatarFilter: {
      ...initialState.avatarFilter,
      loading: true,
    }
  })),

  on(FetchAvatarFilterSuccess, (state, props) => ({
    ...state,
    avatarFilter: {
      avatarOptions: props.avatarOptions,
      loading: false,
    },
  })),

  on(FetchAvatarFilterFailure, (state) => ({
    ...state,
    avatarFilter: {
      ...initialState.avatarFilter,
      loading: false,
    },
  })),
);
